import { useState, ReactElement, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Flex,
  Loader,
  Modal,
  NativeSelect,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import {
  ConfirmationModalFormDataType,
  IConfirmationModalProps,
} from "./types";
import ErrorMessage from "@common/components/text/ErrorMessage";
import { Controller, useForm } from "react-hook-form";
import { customerStore } from "@stores/customer";
import { DEFAULT_CUSTOMER_ADDRESS } from "@stores/customer/constants";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import SelectCountry from "@components/ui/form/SelectCountry";
import { paymentMethodsStore } from "@stores/payment-methods";
import Form from "@common/components/ui/form/Form";
import { purchasesStore } from "@stores/purchases";
import { isSelfHostedPlan, isYearlyPlan } from "@common/utils/plan";
import IconSuccess from "@common/components/icons/IconSuccess";
import { useRouter } from "next/router";
import {
  persistUTMQueryParamsToUrlObject,
  safeRedirectPath,
} from "@common/utils/router";
import WarningCard from "@common/components/thirdParty/mantine/card/WarningCard";
import {
  STRIPE_CHARGE_STATUS_FAILED,
  STRIPE_CHARGE_STATUS_PENDING,
  STRIPE_CHARGE_STATUS_SUCCEEDED,
} from "@stores/purchases/constants";
import IconArrowRight from "@common/components/icons/IconArrowRight";
import IconError from "@common/components/icons/IconError";
import ButtonAsText from "@common/components/thirdParty/mantine/text/ButtonAsText";
import { useCheckPurchasePaymentStatus } from "@hooks/useCheckPurchasePaymentStatus";
import { GoToInstanceLink } from "./ConfirmationModal.components";
import FormLabel from "@common/components/ui/form/FormLabel";
import { useBuildPurchaseContext } from "@addons/hooks";
import { carryOverStorage } from "@addons/utils";
import {
  getSelectItemsFromCountry,
  isAddressValidated,
  isStateFieldNeeded,
} from "@common/utils/data";
import { buildPlanAlias } from "@stores/plans/utils";
import PlanEstimate from "@components/forms/PlanEstimate";
import { IChangePlanData } from "@stores/purchases/types";
import ModalTitle from "@common/components/thirdParty/mantine/modal/ModalTitle";

export default function ConfirmationModal({
  account,
  isYearly,
  plan,
  purchase,
  redirectTo: redirectToOverride,
  opened,
  onClose,
}: IConfirmationModalProps): ReactElement | null {
  const theme = useMantineTheme();
  const router = useRouter();
  const { query } = router;
  const redirectPath =
    safeRedirectPath(redirectToOverride ?? query.redirectTo) ?? "/account";
  const purchaseContext = useBuildPurchaseContext(purchase);

  const [isPayingNow, setIsPayingNow] = useState(false);
  const [editPaymentMethod, setEditPaymentMethod] = useState(true);

  // customer
  const {
    isLoading: loadingCustomerDetails,
    error: customerDetailsError,
    refetch: refetchCustomerDetails,
    data: customerDetails,
  } = customerStore.useGetDetails();

  // payment methods
  const {
    isLoading: loadingDefaultPaymentMethod,
    error: defaultPaymentMethodError,
    data: defaultPaymentMethod,
    refetch: refetchDefaultPaymentMethod,
  } = paymentMethodsStore.useDefaultPaymentMethod();

  const hasPaymentMethod = !!defaultPaymentMethod;

  useEffect(() => {
    if (defaultPaymentMethod) {
      setEditPaymentMethod(false);
    }
  }, [defaultPaymentMethod, setEditPaymentMethod]);

  const {
    isPending: loadingUpdatePaymentMethod,
    error: updatePaymentMethodError,
    mutate: updatePaymentMethod,
  } = paymentMethodsStore.useUpdatePaymentMethod();

  // purchase
  const {
    isPending: loadingChangePlan,
    error: changePlanError,
    mutateAsync: changePurchasePlanAsync,
  } = purchasesStore.useChangePlan(purchase.id);

  // Form
  const {
    register,
    control,
    watch,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<ConfirmationModalFormDataType>({
    shouldFocusError: true,
    shouldUnregister: true,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      ...(customerDetails?.billing_address || DEFAULT_CUSTOMER_ADDRESS),
      name: `${account.first_name} ${account.last_name}`,
    },
  });
  const watchCountry = watch("country");

  useEffect(() => {
    if (opened && !loadingDefaultPaymentMethod && !defaultPaymentMethod) {
      refetchDefaultPaymentMethod();
    } else if (!opened) {
      setIsPayingNow(false);
    }
  }, [
    opened,
    loadingDefaultPaymentMethod,
    defaultPaymentMethod,
    refetchDefaultPaymentMethod,
  ]);

  // Stripe
  const stripe = useStripe();
  const elements = useElements();

  // submit
  const handleConfirm = useCallback(
    async (formData: ConfirmationModalFormDataType) => {
      if (!account || !stripe || !elements) {
        return;
      }

      // 0. Purchase
      const { alias: purchasePlanSlug } = purchase.product.plan;
      const isYearlyPurchase = isYearlyPlan(purchasePlanSlug);

      // 1. Change plan
      const planAlias = buildPlanAlias({
        basePlanAlias: plan.alias,
        isYearly,
        isSelfHosted: isSelfHostedPlan(plan.alias),
      });

      let changePlanSuccess = true;
      if (
        purchasePlanSlug !== planAlias ||
        isYearlyPurchase !== isYearly ||
        hasPaymentMethod
      ) {
        try {
          await changePurchasePlanAsync({
            new_plan_alias: planAlias,
            upsert_add_ons: carryOverStorage(purchaseContext),
            force_end_trial: hasPaymentMethod,
          });
          changePlanSuccess = true;
        } catch (error) {
          changePlanSuccess = false;
        }
      }

      // Stripe
      if (changePlanSuccess) {
        // has already a PM
        // AND not edit the current PM
        if (hasPaymentMethod && !editPaymentMethod) {
          refetchCustomerDetails();
          refetchDefaultPaymentMethod();
          setIsPayingNow(true);
        }
        // Save a PM
        else {
          // 2. Stripe related
          const card = elements.getElement(CardElement);
          if (!card) {
            console.error(
              "CreatePaymentMethodForm, missing Stripe card element",
            );
            return;
          }

          // 3. save customer PM
          const { name, ...address } = formData;
          updatePaymentMethod(
            {
              card,
              stripe,
              billing_details: {
                name,
                email: account.email,
                address,
              },
            },
            {
              onSuccess() {
                refetchCustomerDetails();
                refetchDefaultPaymentMethod();
                setIsPayingNow(true);
              },
            },
          );
        }
      }
    },
    [
      purchaseContext,
      changePurchasePlanAsync,
      plan,
      purchase,
      hasPaymentMethod,
      stripe,
      account,
      elements,
      isYearly,
      updatePaymentMethod,
      editPaymentMethod,
      refetchCustomerDetails,
      refetchDefaultPaymentMethod,
    ],
  );

  // Check payment
  const { data: stripeChargeStatus } = useCheckPurchasePaymentStatus({
    id: purchase.id,
    check: opened && isPayingNow,
  });
  useEffect(() => {
    if (stripeChargeStatus === STRIPE_CHARGE_STATUS_FAILED) {
      refetchDefaultPaymentMethod();
    }
  }, [stripeChargeStatus, refetchDefaultPaymentMethod]);

  // Estimate data
  const changesEstimateData: IChangePlanData = useMemo(() => {
    return { force_end_trial: hasPaymentMethod, new_plan_alias: plan.alias };
  }, [hasPaymentMethod, plan.alias]);

  const showSpinner =
    loadingCustomerDetails ||
    loadingUpdatePaymentMethod ||
    loadingChangePlan ||
    !stripe ||
    !elements;

  return (
    <Modal
      data-testid="SelectPlan-modal"
      centered
      onClose={() => {
        onClose();

        if (
          isPayingNow &&
          stripeChargeStatus === STRIPE_CHARGE_STATUS_SUCCEEDED
        ) {
          router.replace(
            persistUTMQueryParamsToUrlObject(router, redirectPath),
          );
        }
      }}
      opened={opened}
      overflow="inside"
      padding={2 * theme.spacing.md}
      size="lg"
      title={<ModalTitle>Details</ModalTitle>}
    >
      {loadingDefaultPaymentMethod ? (
        <Loader />
      ) : (
        <>
          {isPayingNow ? (
            <Flex
              align="center"
              direction="column"
              gap="md"
              justify="center"
              mah={500}
              mih="50vh"
            >
              {stripeChargeStatus === STRIPE_CHARGE_STATUS_PENDING && (
                <>
                  <Loader size="xl" />
                  <Text c="gray.4" fw={700} fz="xl" mt="md">
                    Payment in progress...
                  </Text>
                </>
              )}
              {stripeChargeStatus === STRIPE_CHARGE_STATUS_FAILED && (
                <>
                  <IconError />
                  <Box mt="md" ta="center">
                    <Text c="gray.7" fw={700} fz="xl">
                      Payment not successful
                    </Text>
                    <Text c="gray.4" mt="xs" mx="auto" w="85%">
                      We could not charge the card you entered, please enter
                      another card or add funds and try again.
                    </Text>
                  </Box>
                  <ButtonAsText
                    onClick={() => {
                      setIsPayingNow(false);
                      refetchDefaultPaymentMethod();
                    }}
                  >
                    <Flex align="baseline" gap="xs" justify="center">
                      <Text span c="brand.4" fw={700} fz="md">
                        Go back
                      </Text>
                      <IconArrowRight />
                    </Flex>
                  </ButtonAsText>
                </>
              )}
              {stripeChargeStatus === STRIPE_CHARGE_STATUS_SUCCEEDED && (
                <>
                  <IconSuccess />
                  <Text c="gray.7" fw={700} fz="xl" mt="md">
                    Payment successful!
                  </Text>
                  <GoToInstanceLink purchase={purchase} />
                </>
              )}
            </Flex>
          ) : (
            <Form
              data-testid="CreatePaymentMethodForm"
              onSubmit={handleSubmit(handleConfirm)}
              disabled={showSpinner}
            >
              <Stack spacing="lg">
                <Card withBorder bg="gray.0" p="md" w="100%">
                  <Flex
                    align="center"
                    direction="row"
                    justify="space-between"
                    wrap="nowrap"
                    w="100%"
                  >
                    <Text c="gray.5" fw={700} fz="lg" m={0}>
                      {plan.name}
                    </Text>
                    <UnstyledButton
                      onClick={onClose}
                      c="brand.4"
                      fw={700}
                      fz="sm"
                      m={0}
                    >
                      Change
                    </UnstyledButton>
                  </Flex>
                </Card>

                {defaultPaymentMethod && (
                  <WarningCard>
                    <Flex
                      align="center"
                      direction="row"
                      gap="md"
                      justify="space-between"
                      wrap="nowrap"
                    >
                      <Text c="gray.6" fz="sm">
                        Your payment method:
                        <Text display={{ base: "none", sm: "inline" }} span>
                          &nbsp;
                        </Text>
                        <Text display={{ base: "inline", sm: "none" }} span>
                          <br />
                        </Text>
                        <b>{defaultPaymentMethod.type}</b> ending in{" "}
                        <b>{defaultPaymentMethod.last4}</b> (
                        {defaultPaymentMethod.expiration}
                        ).
                      </Text>

                      <ButtonAsText
                        fw={700}
                        onClick={() => setEditPaymentMethod(!editPaymentMethod)}
                      >
                        {editPaymentMethod ? "Cancel" : "Edit"}
                      </ButtonAsText>
                    </Flex>
                  </WarningCard>
                )}

                {editPaymentMethod && (
                  <Stack spacing="xs">
                    <TextInput
                      id="name"
                      label="Name on card"
                      placeholder="Name on card"
                      required
                      {...register("name", { required: true })}
                    />

                    <Stack spacing={theme.spacing.xs / 2}>
                      <FormLabel required>Credit card info</FormLabel>
                      <Card id="stripe-form" withBorder p="sm">
                        <CardElement
                          options={{
                            disableLink: true,
                            hidePostalCode: true,
                            style: {
                              base: {
                                fontFamily: theme.fontFamily,
                              },
                            },
                          }}
                        />
                      </Card>

                      <ErrorMessage error={updatePaymentMethodError} />
                    </Stack>

                    <Flex
                      align="flex-end"
                      direction={{ base: "column", xs: "row" }}
                      justify="flex-start"
                      gap={theme.spacing.sm}
                      w="100%"
                    >
                      <TextInput
                        w="100%"
                        id="street"
                        label="Street address"
                        placeholder="123 Main St"
                        required
                        {...register("street", { required: true })}
                      />
                      <TextInput
                        w="100%"
                        id="street2"
                        placeholder="Unit number (optional)"
                        {...register("street2")}
                      />
                    </Flex>

                    <Controller
                      name="country"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { value, onChange } }) => (
                        <SelectCountry
                          data-testid="CreatePaymentMethodForm-country"
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          mt={0}
                        />
                      )}
                    />

                    <Flex
                      align="flex-start"
                      direction={{ base: "column", xs: "row" }}
                      justify="flex-start"
                      gap={theme.spacing.lg}
                      w="100%"
                    >
                      <TextInput
                        w="100%"
                        id="city"
                        label="City"
                        placeholder="City"
                        defaultValue=""
                        required
                        {...register("city", { required: true })}
                      />

                      {isStateFieldNeeded(watchCountry) &&
                        (isAddressValidated(watchCountry) ? (
                          <Controller
                            name="region"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                              <NativeSelect
                                w="100%"
                                data-testid="CreatePaymentMethodForm-region"
                                label="State"
                                data={getSelectItemsFromCountry(watchCountry)}
                                value={value}
                                onChange={(e) => onChange(e.target.value)}
                              />
                            )}
                          />
                        ) : (
                          <TextInput
                            w="100%"
                            id="region"
                            label="State"
                            placeholder="State"
                            {...register("region")}
                          />
                        ))}

                      <TextInput
                        w="100%"
                        id="zip"
                        label="ZIP Code"
                        placeholder="Zipcode"
                        maxLength={20}
                        required
                        {...register("zip", {
                          required: true,
                        })}
                      />
                    </Flex>
                    {errors.zip && (
                      <ErrorMessage m={0} error="Invalid ZIP Code" />
                    )}

                    <ErrorMessage m={0} error={customerDetailsError} />
                  </Stack>
                )}

                <PlanEstimate
                  purchase={purchase}
                  changesData={changesEstimateData}
                />

                <Stack spacing="xs">
                  <Button
                    type="submit"
                    loading={showSpinner}
                    disabled={
                      !defaultPaymentMethod && (!isValid || showSpinner)
                    }
                    w="100%"
                  >
                    Confirm and pay
                  </Button>

                  <ErrorMessage error={defaultPaymentMethodError} />
                  <ErrorMessage error={changePlanError} />
                </Stack>
              </Stack>
            </Form>
          )}
        </>
      )}
    </Modal>
  );
}
